import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as Rx from 'rxjs';
import { AppSettingService } from '../app.service';
import { UserInfoService, IRole, IUserProfile } from '../user/user-info.service';

export const WidgetNames: string[] = [
	"CareerExploration",
	"DNAResponse",
	"InterestSurvey",
	"JobPosting",
	"LandingPage",
	"Login",
	"Map",
	"MyActivity",
	"MyConnection",
	"MyFollowing",
	"MyJourney",
	"MyResource",
	"ProfileDashboard",
	"RecommendedCareer",
	"Signup",
	"SkillProfiler",
	"SkillsGap",
	"StudentPicker",
	"Settings",
	"SystemDashboard",
	"ConnectionClass",
	"EmployerDashboard",
	"MyResumes"
];
export type TWidgetName = typeof WidgetNames[number];

export interface IWidgetLayout {
	ComponentClassName: string,
	UiName: string,
	GridsterItemJson: string,
	SequenceNumber: number,
	IsOn: boolean,
	IsLocked: boolean,
	IsVisible: boolean
}

export interface ISetting {
	Name: string,
	Value: string
}
export interface IWidgetSetting {
	Name: TWidgetName,
	FormattedName: string,
	Enable: boolean,
	Collapse: boolean,
	Setting: ISetting[],
	Layout: IWidgetLayout[],
	HasWidget: boolean
}

export interface IWidgetRole extends IRole {
	Enable: boolean
}

export interface IWidgetHomePage {
	Role: IRole,
	WidgetId: string
	Name: TWidgetName,
	FormattedName?: string;
	newHome?: IWidget
}

export interface IWidget {
	WidgetId: string,
	Name: TWidgetName,
	FormattedName: string,
	Enable: boolean,
	HasHomePage: boolean,
	ManageByRole: boolean,
	Roles: IWidgetRole[]
}

export interface IRoleManagement {
	Widgets: IWidget[],
	HomePages: IWidgetHomePage[]
}

export interface IHomePage {
	Name: string,
	FormattedName: string
	WidgetId: string,
	Selected: boolean
}

export interface IRoleHomePage {
	Role: IRole,
	WidgetId: string,
	HomePages: IHomePage []
}

@Injectable({
	providedIn: 'root'
})
export class WidgetAPIService {
	constructor(
		private http: HttpClient,
		private userInfo: UserInfoService
	) { }

	private widgetUrl = AppSettingService.appConfig.JourneyAPIUrl + "Widget";

	//#region user
	saveUserWidgetCollapse(name: TWidgetName, collapse: boolean): Rx.Observable<any> {
		let data: any = {
			Collapse: collapse,
		};
		return this.http.patch<any>(
			this.widgetUrl + "/User/" + encodeURIComponent(name),
			data,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveUserWidgetSetting(name: TWidgetName, setting: ISetting[]): Rx.Observable<any> {
		let data: any = {
			Setting: setting
		};
		return this.http.patch<any>(
			this.widgetUrl + "/User/" + encodeURIComponent(name),
			data,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveUserWidgetLayout(name: TWidgetName, layout: IWidgetLayout[]): Rx.Observable<any> {
		let data: any = {
			Layout: layout
		};
		return this.http.patch<any>(
			this.widgetUrl + "/User/" + encodeURIComponent(name),
			data,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getUserWidget(name: TWidgetName): Rx.Observable<IWidgetSetting> {
		return this.http.get<IWidgetSetting>(
			this.widgetUrl + "/User/" + encodeURIComponent(name),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	//use this to see available widgets (from outside 2884)
	getUserWidgets(): Rx.Observable<IWidgetSetting> {
		return this.http.get<IWidgetSetting>(
			this.widgetUrl + "/User",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getUserHomePages(): Rx.Observable<IHomePage[]> {
		return this.http.get<any>(
			this.widgetUrl + "/User/HomePage",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveUserHomePage(widgetId: string): Rx.Observable<any> {
		return this.http.post<any>(
			this.widgetUrl + "/User/HomePage/" + encodeURIComponent(widgetId),
			null,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}


	get allowedWidgets(): TWidgetName[] {
		try {
			let p: IUserProfile = this.userInfo.getProfile();
			return p.AllowedWidgets;
		}
		catch (ex) {
			return [];
		}
	}

	isWidgetAllowed(widget: TWidgetName): boolean {
		return this.allowedWidgets.find(x => x === widget) != undefined;
	}
	//#endregion user


	//#region zone
	saveZoneWidgetSetting(name: TWidgetName, setting: ISetting[]): Rx.Observable<any> {
		let data: any = {
			Setting: setting
		};
		return this.http.patch<any>(
			this.widgetUrl + "/Zone/" + encodeURIComponent(name),
			data,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveZoneWidgetLayout(name: TWidgetName, layout: IWidgetLayout[]): Rx.Observable<any> {
		let data: any = {
			Layout: layout
		};
		return this.http.patch<any>(
			this.widgetUrl + "/Zone/" + encodeURIComponent(name),
			data,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getZoneWidget(name: TWidgetName): Rx.Observable<IWidgetSetting> {
		return this.http.get<IWidgetSetting>(
			this.widgetUrl + "/Zone/" + encodeURIComponent(name),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getZoneRoleHomePage(): Rx.Observable<IRoleHomePage[]> {
		return this.http.get<any>(
			this.widgetUrl + "/Zone/HomePage",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveZoneRoleHomePage(homePages: IWidgetHomePage[]): Rx.Observable<any> {
		return this.http.post<any>(
			this.widgetUrl + "/Zone/HomePage",
			homePages,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
	//#endregion zone


	//#region system
	getRoleManagement(zoneId: string): Rx.Observable<IRoleManagement> {
		return this.http.get<IRoleManagement>(
			this.widgetUrl + "/System/RoleManagement/" + encodeURIComponent(zoneId),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveRoleManagement(zoneId: string, roleManagement: IRoleManagement): Rx.Observable<any> {
		return this.http.post<any>(
			this.widgetUrl + "/System/RoleManagement/" + encodeURIComponent(zoneId),
			roleManagement,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveSystemWidgetSetting(zoneId: string, name: TWidgetName, setting: ISetting[]): Rx.Observable<any> {
		let data: any = {
			Setting: setting
		};
		return this.http.patch<any>(
			this.widgetUrl + "/System/" + name + "/" + encodeURIComponent(zoneId),
			data,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveSystemWidgetLayout(zoneId: string, name: TWidgetName, layout: IWidgetLayout[]): Rx.Observable<any> {
		let data: any = {
			Layout: layout
		};
		return this.http.patch<any>(
			this.widgetUrl + "/System/" + name + "/" + encodeURIComponent(zoneId),
			data,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getSystemZoneWidget(zoneId: string, name: TWidgetName): Rx.Observable<IWidgetSetting> {
		return this.http.get<IWidgetSetting>(
			this.widgetUrl + "/System/" + name + "/" + encodeURIComponent(zoneId),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
	//#endregion system
}


